import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line no-restricted-imports
import { BrazeProvider } from '@xing-com/braze';
import { useContentCards } from '@xing-com/crate-common-braze';
import type {
  BrazeContentCard,
  TrackingLibrary,
} from '@xing-com/crate-common-braze';
import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useFeatures, useFeatureSwitch } from '@xing-com/hub';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconJobsFilled,
  IconMessages,
  IconMessagesFilled,
  IconNews,
  IconNewsFilled,
  IconNotificationFilled,
} from '@xing-com/icons';
import { Link } from '@xing-com/link';
import { ProductsMenuWithItemsLoggedIn } from '@xing-com/platform-layout-products-menu';
import ttt from '@xing-com/ticktricktrack';

import { DiscountBadge } from '../discount-badge';
import {
  StyledProductsMenuWrapper,
  ButtonWithMarkerContainer,
  StyledMarker,
} from '../header/header.styled';
import { MeMenuDropdown } from '../me-menu-dropdown';
import { NavItemHeader } from '../nav-item-header';
import { NotificationCenterIcon } from '../notification-center-icon';
import { SearchDropdown } from '../search-dropdown';

import * as Styled from './header-nav-items-logged-in.styled';

const BRAZE_SECTION = 'wbm/boneyard';
const BONEYARD_PLACEMENT = 'boneyard';
const BONEYARD_LOADING_TIMEOUT_MS = 2000;

export const HeaderNavItemsLoggedIn: React.FC = () => {
  return (
    <BrazeProvider transportLayer={ttt} section={BRAZE_SECTION}>
      <HeaderNavItemsLoggedInContent />
    </BrazeProvider>
  );
};

const useBoneyardCard = (): {
  card: BrazeContentCard | undefined;
  loading: boolean;
  tracking: TrackingLibrary | undefined;
} => {
  const contentCards = useContentCards();

  return {
    card: contentCards.cards.reduce<
      (typeof contentCards.cards)[number] | undefined
    >((latestCard, card) => {
      if (card.extras.placement !== BONEYARD_PLACEMENT) {
        return latestCard;
      }

      if (!latestCard) {
        return card;
      }

      return Date.parse(card.updated) > Date.parse(latestCard.updated)
        ? card
        : latestCard;
    }, undefined),
    loading: contentCards.status === 'waiting',
    tracking: contentCards.tracking,
  };
};

const HeaderNavItemsLoggedInContent: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const {
    card: boneyardCard,
    loading: loadingBoneyardCard,
    tracking: contentCardTracking,
  } = useBoneyardCard();
  const boneyardCardExtras = boneyardCard?.extras;
  const [hasBoneyardTimedOut, setHasBoneyardTimedOut] = useState(false);
  const trackedBoneyardImpressionCardIdRef = useRef<string | undefined>(
    undefined
  );
  const boneyardButtonText =
    locale === 'de'
      ? boneyardCardExtras?.buttonTextDe
      : boneyardCardExtras?.buttonTextEn;
  const boneyardMarkerText =
    locale === 'de'
      ? boneyardCardExtras?.markerTextDe
      : boneyardCardExtras?.markerTextEn;

  const { showUpsellHint, showJobSeekerBoneyardUpsellHint } =
    useFeatures()?.data || {};

  const jobsEasyCreateInBoneyardEnabled = useFeatureSwitch(
    'jobs_easy_create_in_boneyard',
    false
  );

  const jobsEasyCreatePath =
    '/recruiting/jobs/create?nwt_nav=boneyard_job_ad&sc_cmp=oow2ac3c767';

  useEffect(() => {
    if (!loadingBoneyardCard || hasBoneyardTimedOut) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHasBoneyardTimedOut(true);
    }, BONEYARD_LOADING_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hasBoneyardTimedOut, loadingBoneyardCard]);

  useEffect(() => {
    if (!boneyardCard || hasBoneyardTimedOut || !contentCardTracking) {
      return;
    }

    if (trackedBoneyardImpressionCardIdRef.current === boneyardCard.id) {
      return;
    }

    contentCardTracking.trackCardImpression(
      BONEYARD_PLACEMENT,
      boneyardCard.id
    );
    trackedBoneyardImpressionCardIdRef.current = boneyardCard.id;
  }, [boneyardCard, contentCardTracking, hasBoneyardTimedOut]);

  const trackBoneyardCardClick = useCallback((): void => {
    if (!boneyardCard || !contentCardTracking) {
      return;
    }

    contentCardTracking.trackCardClick(BONEYARD_PLACEMENT, boneyardCard.id);
  }, [boneyardCard, contentCardTracking]);

  return (
    <>
      <NavItemHeader
        activeItemValues={['jobs']}
        icon={IconJobs}
        activeIcon={IconJobsFilled}
        title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
        href="/jobs/find"
        tracking={{ nwt_nav: 'find_jobs' }}
      />
      <NavItemHeader
        activeItemValues={['my-jobs']}
        icon={IconBookmark}
        activeIcon={IconBookmarkFilled}
        title={formatMessage({ id: 'MAINNAV_MYJOBS' })}
        href="/jobs/my-jobs"
        tracking={{ nwt_nav: 'your_jobs' }}
      />
      <NavItemHeader
        icon={IconContacts}
        activeIcon={IconContactsFilled}
        title={formatMessage({ id: 'MAINNAV_NETWORK' })}
        href="/network"
        activeItemValues={['members']}
        tracking={{ nwt_nav: 'network' }}
      />
      <NavItemHeader
        icon={IconNews}
        activeIcon={IconNewsFilled}
        title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
        href="/discover/updates"
        activeItemValues={['disco']}
        tracking={{ nwt_nav: 'insights' }}
      />
      <NavItemHeader
        icon={IconMessages}
        activeIcon={IconMessagesFilled}
        title={formatMessage({ id: 'MAINNAV_MESSAGES' })}
        href="/chats"
        activeItemValues={['messages']}
        notificationKey="unreadChats"
        tracking={{ nwt_nav: 'messages' }}
      />
      <NavItemHeader
        icon={NotificationCenterIcon}
        activeIcon={IconNotificationFilled}
        passNotificationCountToIcon
        activeItemValues={['supi']}
        notificationKey="ncWeb"
        href="/notifications"
        title={formatMessage({ id: 'NOTIFICATION_CENTER_LINK' })}
        tracking={{ nwt_nav: 'notifications' }}
      />
      <SearchDropdown />
      <MeMenuDropdown />

      {loadingBoneyardCard && !hasBoneyardTimedOut ? (
        <ButtonWithMarkerContainer>
          <Styled.UpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            loading
          >
            {formatMessage({
              id: 'MAINNAV_JOBS_EASY_CREATE',
            })}
          </Styled.UpsellButton>
        </ButtonWithMarkerContainer>
      ) : boneyardCard && !hasBoneyardTimedOut ? (
        <ButtonWithMarkerContainer>
          <Styled.UpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href={boneyardCard.url}
            onClick={trackBoneyardCardClick}
          >
            {boneyardButtonText}
          </Styled.UpsellButton>
          <Link to={boneyardCard.url} onClick={trackBoneyardCardClick}>
            <StyledMarker variant="strong">{boneyardMarkerText}</StyledMarker>
          </Link>
        </ButtonWithMarkerContainer>
      ) : jobsEasyCreateInBoneyardEnabled ? (
        <ButtonWithMarkerContainer>
          <Styled.UpsellButton
            variant="primary"
            size="small"
            data-testid="job-easy-create-button"
            href={jobsEasyCreatePath}
          >
            {formatMessage({
              id: 'MAINNAV_JOBS_EASY_CREATE',
            })}
          </Styled.UpsellButton>
          <Link to={jobsEasyCreatePath}>
            <StyledMarker variant="strong">
              {formatMessage({ id: 'MAINNAV_JOBS_EASY_CREATE_MARKER' })}
            </StyledMarker>
          </Link>
        </ButtonWithMarkerContainer>
      ) : !showUpsellHint ? null : showJobSeekerBoneyardUpsellHint ? (
        <ButtonWithMarkerContainer>
          <Styled.UpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/projobs?reagent=uplt_277"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PROJOBS',
            })}
          </Styled.UpsellButton>

          <DiscountBadge product={ProductType.Projobs} />
        </ButtonWithMarkerContainer>
      ) : (
        <ButtonWithMarkerContainer>
          <Styled.UpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/premium?reagent=uplt_95"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PREMIUM',
            })}
          </Styled.UpsellButton>
          <DiscountBadge product={ProductType.Premium} />
        </ButtonWithMarkerContainer>
      )}
      {
        <StyledProductsMenuWrapper>
          <ProductsMenuWithItemsLoggedIn />
        </StyledProductsMenuWrapper>
      }
    </>
  );
};
